import type { MeView } from './types'

const ME_KEY = 'yc.me'
const ACTING_TENANT_KEY = 'yc.acting-tenant-id'

function read<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

function write(key: string, value: unknown): void {
  try {
    if (value === null || value === undefined) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // localStorage 不可用（隐私模式等）时降级为纯内存态
  }
}

type Listener = () => void

let me: MeView | null = read<MeView>(ME_KEY)
/** null = 未选择（跟随本人租户）；0 = 全部货主 */
let actingTenantId: number | null = read<number>(ACTING_TENANT_KEY)
const listeners = new Set<Listener>()

/**
 * 登录态已被后端确认作废（access 401 且 refresh 也失败）。
 *
 * 存在的理由是打破一个真实死循环：refresh 失败时 failAuth 会同步回调宿主跳登录页，
 * 那次跳转又触发路由守卫，守卫再拉 /me → 401 → 再 refresh → 再跳转……
 * 未登录访问任意受保护页面实测 6 秒打出 5000+ 次请求。这个标记必须在跳转之前置位，
 * 放到调用方（守卫）的 catch 里来不及——那时新导航已经开始了。
 */
let authLost = false

export function isAuthLost(): boolean {
  return authLost
}

export function markAuthLost(): void {
  authLost = true
}

function emit(): void {
  for (const l of listeners) l()
}

export function subscribe(l: Listener): () => void {
  listeners.add(l)
  return () => listeners.delete(l)
}

export function getMe(): MeView | null {
  return me
}

export function setMe(next: MeView): void {
  me = next
  authLost = false
  write(ME_KEY, next)
  emit()
}

export function getActingTenantId(): number | null {
  return actingTenantId
}

export function setActingTenantId(next: number | null): void {
  actingTenantId = next
  write(ACTING_TENANT_KEY, next)
  emit()
}

export function clear(): void {
  me = null
  actingTenantId = null
  write(ME_KEY, null)
  write(ACTING_TENANT_KEY, null)
  emit()
}

export function isOperator(): boolean {
  return me?.tenant.type === 'operator'
}